import { useState, useEffect } from 'react'
import { Link, useSearchParams, useNavigate } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { TrendingUp, AlertCircle, CheckCircle, ArrowLeft, Eye, EyeOff } from 'lucide-react'

export const SetNewPassword = () => {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  // Token catturato UNA VOLTA al mount: subito dopo l'URL viene ripulito
  // per evitare leak via cronologia, shoulder surfing, screenshot o referrer.
  const [token, setToken] = useState<string>('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [tokenMissing, setTokenMissing] = useState(false)

  useEffect(() => {
    const t = searchParams.get('token') || ''
    if (!t) {
      setTokenMissing(true)
      return
    }
    setToken(t)
    // Pulisce l'URL rimuovendo il token dal querystring senza navigare via.
    // Mantiene il path corrente; la History API non scatena re-render del router.
    try {
      window.history.replaceState({}, '', '/set-new-password')
    } catch {
      /* noop: in ambienti sandbox replaceState può essere bloccato */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const validatePassword = (pw: string): string | null => {
    if (pw.length < 8) return 'La password deve contenere almeno 8 caratteri.'
    if (pw.length > 72) return 'La password è troppo lunga (max 72 caratteri).'
    if (!/[A-Za-z]/.test(pw)) return 'La password deve contenere almeno una lettera.'
    if (!/[0-9]/.test(pw)) return 'La password deve contenere almeno un numero.'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const pwError = validatePassword(password)
    if (pwError) {
      setError(pwError)
      return
    }

    if (password !== confirmPassword) {
      setError('Le due password non coincidono.')
      return
    }

    setIsLoading(true)
    try {
      const response = await fetch('/api/v1/auth/reset-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      })

      if (response.status === 429) {
        setError('Troppi tentativi. Riprova tra qualche minuto.')
        return
      }

      if (!response.ok) {
        const data = await response.json().catch(() => ({}))
        setError(data.detail || 'Errore durante il reset. Richiedi un nuovo link.')
        return
      }

      setSuccess(true)
      setTimeout(() => navigate('/login'), 3000)
    } catch (err) {
      setError('Errore di rete. Verifica la connessione e riprova.')
    } finally {
      setIsLoading(false)
    }
  }

  if (tokenMissing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-10 w-10 text-primary" />
                <span className="text-2xl font-bold">iXen</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <div className="flex justify-center">
              <div className="rounded-full bg-destructive/10 p-3">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
            </div>
            <h3 className="text-lg font-semibold">Link non valido</h3>
            <p className="text-sm text-muted-foreground">
              Il link di reset non è valido o è incompleto. Richiedi un nuovo link
              dalla pagina di recupero password.
            </p>
            <Link to="/reset-password">
              <Button className="w-full">Richiedi nuovo link</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4">
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-2">
                <TrendingUp className="h-10 w-10 text-primary" />
                <span className="text-2xl font-bold">iXen</span>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4 text-center">
            <div className="flex justify-center">
              <div className="rounded-full bg-green-500/10 p-3">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
            </div>
            <h3 className="text-lg font-semibold">Password aggiornata</h3>
            <p className="text-sm text-muted-foreground">
              La tua password è stata cambiata con successo. Verrai reindirizzato
              al login...
            </p>
            <Link to="/login">
              <Button className="w-full" variant="outline">
                Vai al login ora
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-4">
          <div className="flex items-center justify-center">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-10 w-10 text-primary" />
              <span className="text-2xl font-bold">iXen</span>
            </div>
          </div>
          <div className="text-center">
            <CardTitle className="text-2xl">Imposta nuova password</CardTitle>
            <CardDescription>
              Scegli una password sicura: almeno 8 caratteri, con lettere e numeri.
            </CardDescription>
          </div>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="flex items-center space-x-2 p-3 bg-destructive/10 text-destructive rounded-md">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="password">Nuova password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                  minLength={8}
                  maxLength={72}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Conferma password</Label>
              <Input
                id="confirmPassword"
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={8}
                maxLength={72}
              />
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !password || !confirmPassword}
            >
              {isLoading ? 'Aggiornamento in corso...' : 'Aggiorna password'}
            </Button>

            <div className="text-center">
              <Link to="/login" className="text-sm text-muted-foreground hover:text-foreground">
                <ArrowLeft className="w-3 h-3 inline mr-1" />
                Torna al login
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
